import React, { FC, useCallback } from 'react';
import LoadingButton, { LoadingButtonProps } from '@mui/lab/LoadingButton';
import { styled } from '@mui/material/styles';

import { useTranslate } from 'src/shared/providers';

interface Props extends Omit<LoadingButtonProps, 'onClick' | 'onError'> {
  onClick: (file: File) => void;
  maxFileSize?: number; // in mb
  accept?: string;
  onError?: (message: string) => void;
}

export const FileUploadButton: FC<Props> = ({
  onClick,
  maxFileSize,
  accept = '.png,.jpg,.jpeg,.gif',
  children,
  onError,
  className,
  ...props
}) => {
  const { t } = useTranslate();
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!event.target.files) {
        return;
      }

      const file: File | undefined = event.target.files[0];

      if (!file) {
        const message = t('files.somethingWentWrongWhenUploadingYourFilePleaseTryAgain');
        if (onError) onError(message);
        return;
      }

      const acceptableFileTypes = accept.split(',');
      if (
        acceptableFileTypes.length &&
        !acceptableFileTypes.some((type) => file.type.includes(type.replace(/[^a-zA-Z ]/g, '')))
      ) {
        const message = t('files.yourFileTypeIsNotAllowed');
        if (onError) onError(message);
        return;
      }

      if (maxFileSize) {
        const fileSize = file.size / 1024 / 1024; // in mb

        if (fileSize > maxFileSize) {
          const message = t('files.yourFileSizeOfLimitMbExceedsTheSizeLimit', {
            limit: fileSize.toFixed(2),
            size: maxFileSize,
          });
          if (onError) onError(message);
          return;
        }
      }

      event.target.value = '';
      onClick(file);
    },
    [maxFileSize, onClick, onError],
  );

  const handleSubmission = () => {
    if (!inputRef.current) {
      return;
    }

    inputRef.current.click();
  };

  return (
    <div className={className}>
      <LoadingButton variant="contained" onClick={() => handleSubmission()} {...props}>
        {children}
      </LoadingButton>
      <StyledHiddenInput ref={inputRef} type="file" name="file" accept={accept} onChange={handleChange} />
    </div>
  );
};

const StyledHiddenInput = styled('input')(() => ({
  display: 'none',
}));
